import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/FeedbackSession.css';
import { ReactComponent as DirectoryLogo } from '../images/directory.svg';
import { ReactComponent as ArrowLogo } from '../images/arrow.svg';
import StarRating from './components/StarRating';
import './styles/LoadingIndicator.css'; // Import loading indicator styles
import { API_URLS } from '../config'; // Import API URLs

// Component to show the sentiment tag with appropriate color
const SentimentTag = ({ sentiment }) => {
  // Don't show sentiment tag if data is not available
  if (!sentiment || sentiment === null || sentiment === 'unknown') {
    return null;
  }
  
  const sentimentColors = {
    positive: '#10b981', // Green
    negative: '#ef4444'  // Red
  };
  
  const sentimentEmojis = {
    positive: '😊',
    negative: '😞'
  };
  
  const style = {
    backgroundColor: sentimentColors[sentiment] || '#6b7280',
  };
  
  return (
    <span className="sentiment-tag" style={style}>
      {sentimentEmojis[sentiment]} {sentiment ? sentiment.charAt(0).toUpperCase() + sentiment.slice(1) : 'Unknown'}
    </span>
  );
};

// Component to show usefulness badge
const UsefulnessBadge = ({ usefulnessScore, usefulnessLabel }) => {
  // Don't show badge if data is not available or is default value
  if (!usefulnessScore || usefulnessScore === 0 || usefulnessScore === null || usefulnessScore === undefined) {
    return (
      <span className="usefulness-badge processing">
        ⏳ Analysis Pending
      </span>
    );
  }
  
  const score = parseFloat(usefulnessScore);
  let badgeClass = 'usefulness-badge';
  let icon = '';
  let text = usefulnessLabel || 'Not Useful';
  
  if (score >= 0.8) {
    badgeClass += ' very-valuable';
    icon = '💎';
    text = 'Very Valuable';
  } else if (score >= 0.6) {
    badgeClass += ' useful';
    icon = '⭐';
    text = 'Useful';
  } else {
    badgeClass += ' not-useful';
    icon = '📝';
    text = 'Standard';
  }
  
  return (
    <span className={badgeClass}>
      {icon} {text} ({(score * 100).toFixed(0)}%)
    </span>
  );
};

// Main FeedbackSession component
const FeedbackSession = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const businessId = localStorage.getItem('businessId');
  const token = localStorage.getItem('token'); // Get token for authorization
  
  // Format date in a readable way
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(date).toLocaleDateString(undefined, options);
  };
  
  // Fetch session data
  useEffect(() => {
    if (!businessId || !submissionId) {
      setError('Missing business ID or submission ID');
      setLoading(false);
      return;
    }
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      // Optionally redirect to login
      // navigate('/login');
      return;
    }

    const fetchSessionData = async () => {
      try {
        setLoading(true);
        setError(null); // Reset error state

        const response = await fetch(API_URLS.feedbackSession(businessId, submissionId), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' // Optional: Add content type if needed
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Feedback session not found.');
          } else if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized. Please check your login.');
          } else {
            throw new Error(`Failed to fetch session data: ${response.status} ${response.statusText}`);
          }
        }

        const data = await response.json();
        console.log('Session data:', data); // Debugging log
        setSession(data);

      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(err.message || 'Failed to load feedback session');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [businessId, submissionId, token, navigate]);
  
  // Effect for glass box mouse move animation
  useEffect(() => {
    // Find all glass-box elements after the component mounts
    const glassBoxes = document.querySelectorAll('.glass-box');

    // Define the event handler functions
    const handleMouseMove = (e) => {
      const element = e.currentTarget;
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      element.style.setProperty('--mouse-x', `${x}px`);
      element.style.setProperty('--mouse-y', `${y}px`);
    };

    const handleMouseLeave = (e) => {
      const element = e.currentTarget;
      element.style.removeProperty('--mouse-x');
      element.style.removeProperty('--mouse-y');
    };

    // Add event listeners to each element
    glassBoxes.forEach(element => {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseleave', handleMouseLeave);
    });

    // Cleanup function: Remove event listeners when the component unmounts
    return () => {
      glassBoxes.forEach(element => {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [session]); // Rerun when session changes so new elements get handlers
  return (
    <div className="feedback-session-content">
      {/* Header with breadcrumb navigation - Always visible */}
      <div className="session-header">
        <div className="directory-info">
          <div className="directory-logo">
            <DirectoryLogo className="arrow-logo" />
            <p>Dashboard</p>
            <ArrowLogo />
            <p>Reviews</p>
            <ArrowLogo />
            <p>Session {submissionId.slice(0, 8)}...</p>
          </div>
        </div>
        <h1>Feedback Session</h1>
        <div className="session-meta">
          <div className="session-date">
            {!loading && session?.submission_date ? formatDate(session.submission_date) : 'Date unavailable'}
          </div>
          <div className="session-id">
            ID: {submissionId}
          </div>
        </div>
      </div>

      {/* Loading state - Appears after the header */}
      {loading ? (
        <div className="session-loading">
          <div className="session-loading-spinner"></div>
          <p>Loading feedback session data...</p>
        </div>
      ) : error ? (
        <div className="session-error">
          <h2>Error Loading Feedback Session</h2>
          <p>{error}</p>
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      ) : !session ? (
        <div className="session-not-found">
          <h2>Feedback Session Not Found</h2>
          <p>The requested feedback session could not be found.</p>
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      ) : (
        /* Main content grid - Only shown after data is loaded */
        <div className="session-grid">
          {/* Left column */}
          <div className="session-left-column">
            {/* Overall rating card */}
            <div className="overall-rating-container glass-box">
              <h2>Overall Rating</h2>
              <div className="rating-display">
                <div className="rating-number">{session.general_rating?.rating_value.toFixed(1)}</div>
                <StarRating rating={session.general_rating?.rating_value || 0} size="large" />
              </div>
            </div>

            {/* Text feedback card */}
            <div className="text-feedback-container glass-box">
              <div className="feedback-header">
                <h2>Customer Feedback</h2>
                <div className="feedback-badges">
                  {session.analysis?.sentiment_label && (
                    <SentimentTag sentiment={session.analysis.sentiment_label} />
                  )}
                  <UsefulnessBadge 
                    usefulnessScore={session.analysis?.usefulness_score} 
                    usefulnessLabel={session.analysis?.usefulness_label}
                  />
                </div>
              </div>
              <div className="feedback-text">
                {session.text_feedback?.feedback_text || 'No text feedback provided.'}
              </div>
              {session.analysis?.extracted_keywords && session.analysis.extracted_keywords.length > 0 && (
                <div className="extracted-keywords">
                  <h3>Key Themes</h3>
                  <div className="keywords-list">
                    {session.analysis.extracted_keywords.map((keyword, index) => (
                      <span key={index} className="keyword-tag">{keyword}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* AI Analysis card *
            <div className="ai-analysis-container glass-box">
              <h2>AI Insights</h2>
              <div className="ai-insights">
                <p>
                  {session.analysis?.ai_insights || 
                    'No AI analysis available. Click "Generate Analysis" to create insights from this feedback.'}
                </p>
              </div>
              <button className="generate-analysis-btn">
                Generate Analysis
              </button>
            </div>*/}
          </div>

          {/* Right column */}
          <div className="session-right-column">
            {/* Question ratings card */}
            <div className="question-ratings-container glass-box">
              <h2>Detailed Ratings</h2>
              {session.question_ratings && session.question_ratings.length > 0 ? (
                <div className="question-ratings-list">
                  {session.question_ratings.map((item, index) => (
                    <div key={index} className="question-rating-item">
                      <div className="question-text">
                        {item.question_text}
                      </div>
                      <div className="question-rating">
                        <StarRating rating={item.rating_value} size="small" />
                        <span className="rating-value">{item.rating_value.toFixed(1)}</span>
                      </div>
                      {item.topic_name && (
                        <div className="question-topic">
                          <span className="topic-badge">{item.topic_name}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-question-ratings">
                  No detailed ratings available for this session.
                </div>
              )}
            </div>

            {/* Comparison card */}
            <div className="comparison-container glass-box">
              <h2>Performance Comparison</h2>
              <div className="comparison-metrics">
                <div className="comparison-metric">
                  <span className="metric-label">Vs. Business Average</span>
                  <div className={`metric-value ${session.general_rating?.vs_average > 0 ? 'positive' : session.general_rating?.vs_average < 0 ? 'negative' : 'neutral'}`}>
                    {session.general_rating?.vs_average > 0 ? '+' : ''}{session.general_rating?.vs_average?.toFixed(1) || 'N/A'}
                  </div>
                </div>
                <div className="comparison-metric">
                  <span className="metric-label">Vs. Last Month</span>
                  <div className={`metric-value ${session.general_rating?.vs_last_month > 0 ? 'positive' : session.general_rating?.vs_last_month < 0 ? 'negative' : 'neutral'}`}>
                    {session.general_rating?.vs_last_month > 0 ? '+' : ''}{session.general_rating?.vs_last_month?.toFixed(1) || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackSession;
